import React, { createContext, useContext, useReducer } from "react";

const context = createContext<{
  state: IDialogContext;
  dispatch: React.ActionDispatch<[DialogActions]>;
}>({
  state: {
    dialogs: {},
  },
  dispatch: () => {},
});

interface Dialog {
  WELCOME: object;
  CHAT_INFO: object;
  LOGIN_INFO:
    | {
        mode: "INHIBIT" | "TRUST";
      }
    | { mode: "NORMAL"; url: string };
  EVENT_INFO: object;
  KEYBINDS: object;
  MAILING_LISTS: object;
  SHARE_MODAL: object;
}

interface IDialogContext {
  dialogs: { [id in keyof Dialog]?: Dialog[id] };
}

export type DialogData<K extends keyof Dialog> = Dialog[K];

type DialogActions =
  | {
      action: "OPEN_DIALOG";
      dialog: {
        [Id in keyof Dialog]: ({ id: Id } & Dialog[Id]) & {};
      }[keyof Dialog];
    }
  | { action: "CLOSE_DIALOG"; dialogId: keyof Dialog };

export const useDialog = () => useContext(context);

export const DialogContext = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = useReducer<IDialogContext, [DialogActions]>(
    (state, data) => {
      switch (data.action) {
        case "OPEN_DIALOG": {
          const { id, ...ddata } = data.dialog;

          return {
            ...state,
            dialogs: {
              ...state.dialogs,
              [id]: ddata,
            },
          };
        }
        case "CLOSE_DIALOG":
          return {
            ...state,
            dialogs: Object.fromEntries(
              Object.entries(state.dialogs).filter(
                ([k]) => k !== data.dialogId,
              ),
            ),
          };
      }

      throw new Error("Unknown DialogContext action: " + JSON.stringify(data));
    },
    {
      dialogs: {
        EVENT_INFO: {},
      },
    },
  );

  return (
    <context.Provider value={{ state, dispatch }}>{children}</context.Provider>
  );
};
